import { Box } from '@mui/material';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { SGR_STATES, SGR_TYPES } from 'lib/consts';
import { formatDate } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import PaymentDocument from 'pages/Finance/Components/Financing/SGR/components/PaymentDocument';
import GeneralInfo from 'pages/Finance/Components/Financing/SGR/components/Summary/Components/GeneralInfo';
import ImportantDates from 'pages/Finance/Components/Financing/SGR/components/Summary/Components/ImportantDates';
import Invoicing from 'pages/Finance/Components/Financing/SGR/components/Summary/Components/Invoicing';
import PrincipalInfo from 'pages/Finance/Components/Financing/SGR/components/Summary/Components/PrincipalInfo';
import React from 'react';

import RiskPremiumCalculator from './RiskPremiumCalculator';

const WarrantySummary: React.FC<{
  warranty: Warranty & { business?: { primary?: string; secondary?: string } };
}> = ({ warranty }) => {
  if (!warranty) return null;

  const warrantyType =
    SGR_TYPES.find((type) => type.value === warranty.type)?.label ??
    warranty.type;
  const warrantyStatus = SGR_STATES.find(
    (state) => state.value === warranty.status,
  );

  const showDocument =
    warranty?.status === 'APPROVED' || warranty?.status === 'ACTIVE';

  const showCalculator =
    warranty?.status === 'QUOTED' || warranty?.status === 'IN_REVIEW';

  const isActive = warranty?.status === 'ACTIVE';

  return (
    <Stack direction="row" spacing={1}>
      <Stack direction="column" spacing={1}>
        <GeneralInfo
          warrantyStatus={warrantyStatus}
          warrantyType={warrantyType}
          quotedAmount={warranty.quotedAmount}
          quotedCurrency={warranty.quotedCurrency as 'CLP' | 'USD' | 'UF'}
          tenderId={warranty.tenderId}
          description={warranty.description ?? 'Sin descripción'}
          business={warranty.business}
          fogape={warranty.fogape}
        />

        <ImportantDates
          quoteDate={formatDate(new Date(warranty.quoteDate), 'dd-mm-yyyy')}
          startDate={formatDate(new Date(warranty.startDate), 'dd-mm-yyyy')}
          endDate={formatDate(new Date(warranty.endDate), 'dd-mm-yyyy')}
        />

        <PrincipalInfo warranty={warranty} />
      </Stack>

      <Stack direction="column" spacing={1}>
        {showDocument && <PaymentDocument warranty={warranty} />}
        {isActive && <Invoicing id={warranty.id} pruffConnect={true} />}
      </Stack>

      {showCalculator && (
        <Box sx={{ mt: 3 }}>
          <RiskPremiumCalculator
            amount={warranty.quotedAmount}
            startDate={dayjs(warranty.startDate)}
            endDate={dayjs(warranty.endDate)}
            quotedCurrency={warranty.quotedCurrency}
            type={warranty.type}
            warrantyRates={{
              variableRate: warranty.operationCostVariable ?? null,
              fixedRate: warranty.operationCostFixed ?? null,
              variableDiscount: warranty.discountRateVariable ?? null,
              fixedDiscount: warranty.discountRateFixed ?? null,
            }}
          />
        </Box>
      )}
    </Stack>
  );
};

export default WarrantySummary;
